import { nanoid } from "nanoid";

const createResult = (success, data, message) => ({
  success,
  data,
  message,
  error: success ? null : message,
});

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};
const asArray = (value) => (Array.isArray(value) ? value : []);

const inferActionKind = (activity) => {
  const type = typeof activity.type === "string" ? activity.type : "";
  if (type === "externalEvent") return "external_event";
  if (
    [
      "action",
      "activity",
      "interaction",
      "external_event",
      "external",
      "trigger",
      "start-role",
      "end-role",
      "case",
      "part",
      "repeat",
      "ellipsis",
      "sync",
      "void",
      "refinement",
    ].includes(type)
  ) {
    return type;
  }
  if (activity.class === "refinement" && ["case", "part", "repeat"].includes(type)) {
    return type;
  }
  if (activity.class === "refinement") return "refinement";
  return "activity";
};

const actionKindToLegacyType = (kind) => {
  if (kind === "external_event")
    return { class: "activity", type: "externalEvent" };
  if (["case", "part", "repeat", "refinement"].includes(kind)) {
    return { class: "refinement", type: kind };
  }
  return { class: "activity", type: kind || "activity" };
};

const buildStatePlacement = (activities, threadById) => {
  const placement = new Map();
  activities.forEach((activity) => {
    const threadId = typeof activity.thread_id === "string" ? activity.thread_id : "";
    const thread = threadById.get(threadId);
    if (!thread) return;
    const pre = typeof activity.prestate_id === "string" ? activity.prestate_id : null;
    const post = typeof activity.poststate_id === "string" ? activity.poststate_id : null;
    if (pre && !placement.has(pre)) placement.set(pre, { threadId, roleId: thread.roleId });
    if (post && !placement.has(post))
      placement.set(post, { threadId, roleId: thread.roleId });
  });
  return placement;
};

export const legacyToVnext = (legacyInput) => {
  const legacy = asObject(legacyInput);
  const roles = asArray(legacy.roles).map((role) => ({
    id: String(role.id ?? ""),
    label: String(role.label ?? ""),
    transient: role.transient === undefined ? undefined : Boolean(role.transient),
    multiple: role.multiple === undefined ? undefined : Boolean(role.multiple),
    position: asObject(role.position),
    size: asObject(role.size),
  }));
  const threads = asArray(legacy.threads).map((thread) => ({
    id: String(thread.id ?? ""),
    roleId: String(thread.role_id ?? ""),
    class: ["case", "part", "repeat"].includes(thread.class)
      ? thread.class
      : "thread",
    label: typeof thread.label === "string" ? thread.label : undefined,
    parentActionId: typeof thread.parent_id === "string" ? thread.parent_id : undefined,
    index: typeof thread.index === "number" ? thread.index : undefined,
  }));
  const threadById = new Map(threads.map((thread) => [thread.id, thread]));
  const activitiesRaw = asArray(legacy.activities);
  const statePlacement = buildStatePlacement(activitiesRaw, threadById);
  const states = asArray(legacy.states).map((state) => {
    const stateId = String(state.id ?? "");
    const inferred = statePlacement.get(stateId);
    return {
      id: stateId,
      label: typeof state.label === "string" ? state.label : undefined,
      show: typeof state.show === "boolean" ? state.show : undefined,
      threadId: inferred?.threadId ?? "",
      roleId: inferred?.roleId ?? "",
      flags: {
        start: state.start === true,
        end: state.end === true,
      },
    };
  });
  const activities = activitiesRaw.map((activity) => {
    const threadId = String(activity.thread_id ?? "");
    const thread = threadById.get(threadId);
    return {
      id: String(activity.id ?? ""),
      roleId: thread?.roleId ?? "",
      threadId,
      kind: inferActionKind(activity),
      pre: String(activity.prestate_id ?? ""),
      post: String(activity.poststate_id ?? ""),
      label: typeof activity.label === "string" ? activity.label : undefined,
    };
  });
  const connections = asArray(legacy.connections).map((connection) => ({
    id: String(connection.id ?? ""),
    interactionIds: asArray(connection.interactions).map((id) => String(id)),
  }));
  return {
    id: typeof legacy.id === "string" ? legacy.id : undefined,
    status: typeof legacy.status === "string" ? legacy.status : undefined,
    version: typeof legacy.version === "number" ? legacy.version : 0,
    info: {
      ...asObject(legacy.info),
      ...(typeof asObject(legacy.info).targetEngine === "string"
        ? {}
        : { targetEngine: "reachability" }),
    },
    roles,
    threads,
    states,
    activities,
    connections,
  };
};

export const vnextToLegacy = (rad, baselineInput) => {
  const baseline = asObject(baselineInput);
  const roles = rad.roles.map((role) => ({
    ...asObject(asArray(baseline.roles).find((r) => asObject(r).id === role.id)),
    id: role.id,
    class: "role",
    label: role.label,
    transient: role.transient ?? true,
    multiple: role.multiple ?? false,
    ...(role.position ? { position: role.position } : {}),
    ...(role.size ? { size: role.size } : {}),
  }));
  const threads = rad.threads.map((thread) => ({
    ...asObject(asArray(baseline.threads).find((t) => asObject(t).id === thread.id)),
    id: thread.id,
    class: thread.class,
    role_id: thread.roleId,
    ...(thread.label ? { label: thread.label } : {}),
    ...(thread.parentActionId ? { parent_id: thread.parentActionId } : {}),
    ...(typeof thread.index === "number" ? { index: thread.index } : {}),
  }));
  const states = rad.states.map((state) => ({
    ...asObject(asArray(baseline.states).find((s) => asObject(s).id === state.id)),
    id: state.id,
    class: "state",
    type: "state",
    label: state.label ?? "",
    show: state.show ?? false,
  }));
  const activities = rad.activities.map((action) => {
    const mapped = actionKindToLegacyType(action.kind);
    return {
      ...asObject(asArray(baseline.activities).find((a) => asObject(a).id === action.id)),
      id: action.id,
      class: mapped.class,
      type: mapped.type,
      thread_id: action.threadId,
      prestate_id: action.pre,
      poststate_id: action.post,
      ...(action.label ? { label: action.label } : {}),
    };
  });
  const connections = rad.connections.map((connection) => ({
    ...asObject(asArray(baseline.connections).find((c) => asObject(c).id === connection.id)),
    id: connection.id,
    class: "connection",
    interactions: connection.interactionIds,
  }));
  return {
    ...baseline,
    id: rad.id ?? baseline.id,
    status: rad.status ?? baseline.status,
    version: rad.version,
    info: rad.info,
    roles,
    threads,
    states,
    activities,
    connections,
  };
};

const bumpVersion = (rad) => ({ ...rad, version: (rad.version ?? 0) + 1 });
const ensureUnique = (ids) => Array.from(new Set(ids));

const addRoleVnext = (radInput, payload) => {
  if (!payload?.label || typeof payload.label !== "string") {
    return { ok: false, error: { message: "label is required" } };
  }
  const rad = legacyToVnext(radInput);
  const roleId = payload.id ?? nanoid();
  if (rad.roles.some((role) => role.id === roleId)) {
    return { ok: false, error: { message: `role ${roleId} already exists` } };
  }
  if (rad.roles.some((role) => role.label === payload.label)) {
    return {
      ok: false,
      error: { message: `role label ${payload.label} must be unique` },
    };
  }
  return {
    ok: true,
    value: bumpVersion({
      ...rad,
      roles: [
        ...rad.roles,
        {
          id: roleId,
          label: payload.label,
          transient: payload.transient,
          multiple: payload.multiple,
          position: payload.position,
          size: payload.size,
        },
      ],
    }),
  };
};

const addActivityVnext = (radInput, payload) => {
  const rad = legacyToVnext(radInput);
  const thread = rad.threads.find((candidate) => candidate.id === payload.threadId);
  if (!thread) return { ok: false, error: { message: `thread ${payload.threadId} not found` } };
  const pre = rad.states.find((state) => state.id === payload.pre);
  const post = rad.states.find((state) => state.id === payload.post);
  if (!pre || !post) {
    return { ok: false, error: { message: "pre and post states must exist" } };
  }
  const actionId = payload.id ?? nanoid();
  if (rad.activities.some((action) => action.id === actionId)) {
    return { ok: false, error: { message: `action ${actionId} already exists` } };
  }
  return {
    ok: true,
    value: bumpVersion({
      ...rad,
      activities: [
        ...rad.activities,
        {
          id: actionId,
          roleId: thread.roleId,
          threadId: thread.id,
          kind: payload.kind ?? "activity",
          pre: payload.pre,
          post: payload.post,
          label: payload.label,
        },
      ],
    }),
  };
};

const addInteractionVnext = (radInput, payload) => {
  const rad = legacyToVnext(radInput);
  const source = rad.activities.find((action) => action.id === payload.sourceActionId);
  const target = rad.activities.find((action) => action.id === payload.targetActionId);
  if (!source || !target) {
    return {
      ok: false,
      error: { message: "interaction source and target actions must exist" },
    };
  }
  if (source.kind !== "interaction" || target.kind !== "interaction") {
    return {
      ok: false,
      error: { message: "source and target actions must be interactions" },
    };
  }

  const existing = rad.connections.find(
    (connection) =>
      connection.interactionIds.includes(source.id) &&
      connection.interactionIds.includes(target.id)
  );
  if (existing) return { ok: true, value: rad };

  const firstConnected = rad.connections.find(
    (connection) =>
      connection.interactionIds.includes(source.id) ||
      connection.interactionIds.includes(target.id)
  );
  if (firstConnected) {
    return {
      ok: true,
      value: bumpVersion({
        ...rad,
        connections: rad.connections.map((connection) =>
          connection.id !== firstConnected.id
            ? connection
            : {
                ...connection,
                interactionIds: ensureUnique([
                  ...connection.interactionIds,
                  source.id,
                  target.id,
                ]),
              }
        ),
      }),
    };
  }

  const connectionId = payload.connectionId ?? nanoid();
  return {
    ok: true,
    value: bumpVersion({
      ...rad,
      connections: [
        ...rad.connections,
        { id: connectionId, interactionIds: [source.id, target.id] },
      ],
    }),
  };
};

export const addThreadLegacy = (legacyRad, payload = {}) => {
  const next = asObject(legacyRad);
  const roleId = payload.role_id;
  if (!roleId) return createResult(false, null, "role_id required");
  const role = asArray(next.roles).find((candidate) => candidate.id === roleId);
  if (!role) return createResult(false, null, `role ${roleId} not found`);
  const threadId = payload.id || nanoid();
  const prestateId = payload.prestate_id ?? nanoid();
  const poststateId = payload.poststate_id ?? nanoid();
  const states = [...asArray(next.states)];
  if (!states.some((state) => state.id === prestateId)) {
    states.push({
      id: prestateId,
      class: "state",
      type: "state",
      label: "START",
      show: false,
    });
  }
  if (!states.some((state) => state.id === poststateId)) {
    states.push({
      id: poststateId,
      class: "state",
      type: "state",
      label: "END",
      show: false,
    });
  }
  const threads = [...asArray(next.threads)];
  threads.push({
    id: threadId,
    class: payload.className || "thread",
    role_id: roleId,
    label: payload.label,
    prestate_id: prestateId,
    poststate_id: poststateId,
    ...(payload.position ? { position: payload.position } : {}),
  });
  return createResult(
    true,
    {
      ...next,
      version: typeof next.version === "number" ? next.version + 1 : 1,
      states,
      threads,
    },
    `Successfully added thread ${threadId}`
  );
};

export const addConnectionLegacy = (legacyRad, payload = {}) => {
  const next = asObject(legacyRad);
  const interactions = Array.isArray(payload.interactions) ? payload.interactions : [];
  if (interactions.length < 2) {
    return createResult(false, null, "at least two interactions are required");
  }
  const uniqueInteractions = Array.from(new Set(interactions));
  const connections = [...asArray(next.connections)];
  const existing = connections.find((connection) =>
    asArray(connection.interactions).some((id) => uniqueInteractions.includes(id))
  );
  if (existing) {
    const mergedConnections = connections.map((connection) =>
      connection.id === existing.id
        ? {
            ...connection,
            interactions: Array.from(
              new Set([...asArray(connection.interactions), ...uniqueInteractions])
            ),
          }
        : connection
    );
    return createResult(
      true,
      {
        ...next,
        version: typeof next.version === "number" ? next.version + 1 : 1,
        connections: mergedConnections,
      },
      `Successfully merged interactions into connection ${existing.id}`
    );
  }

  return createResult(
    true,
    {
      ...next,
      version: typeof next.version === "number" ? next.version + 1 : 1,
      connections: [
        ...connections,
        {
          id: payload.id || nanoid(),
          class: payload.class || "connection",
          interactions: uniqueInteractions,
        },
      ],
    },
    "Successfully added connection"
  );
};

export const addRoleLegacy = (legacyRad, payload = {}) => {
  const result = addRoleVnext(legacyRad, payload);
  if (!result.ok) return createResult(false, null, result.error.message);
  return createResult(
    true,
    vnextToLegacy(result.value, legacyRad),
    `Successfully added role "${payload.label}"`
  );
};

export const addActivityLegacy = (legacyRad, payload = {}) => {
  const target = asObject(payload.target);
  const activity = asObject(payload.activity);
  if (target.classname === "role") {
    if (typeof target.id !== "string") {
      return createResult(false, null, "role target id is required");
    }
    const roleId = target.id;
    const next = asObject(legacyRad);
    const role = asArray(next.roles).find((candidate) => candidate.id === roleId);
    if (!role) return createResult(false, null, `role ${roleId} not found`);
    const threadId = nanoid();
    const prestateId =
      typeof activity.prestate_id === "string" ? activity.prestate_id : nanoid();
    const poststateId =
      typeof activity.poststate_id === "string" ? activity.poststate_id : nanoid();
    const states = [...asArray(next.states)];
    if (!states.some((state) => state.id === prestateId)) {
      states.push({
        id: prestateId,
        class: "state",
        type: "state",
        label: "START",
        show: false,
      });
    }
    if (!states.some((state) => state.id === poststateId)) {
      states.push({
        id: poststateId,
        class: "state",
        type: "state",
        label: "END",
        show: false,
      });
    }
    const threads = [...asArray(next.threads)];
    threads.push({
      id: threadId,
      class: "thread",
      role_id: roleId,
      label: typeof activity.threadLabel === "string" ? activity.threadLabel : undefined,
      prestate_id: prestateId,
      poststate_id: poststateId,
    });
    const kind = inferActionKind(activity);
    const mapped = actionKindToLegacyType(kind);
    const activities = [...asArray(next.activities)];
    activities.push({
      id: typeof activity.id === "string" ? activity.id : nanoid(),
      class: mapped.class,
      type: mapped.type,
      label: typeof activity.label === "string" ? activity.label : `New ${mapped.type}`,
      thread_id: threadId,
      prestate_id: prestateId,
      poststate_id: poststateId,
    });
    return createResult(
      true,
      {
        ...next,
        version: typeof next.version === "number" ? next.version + 1 : 1,
        states,
        threads,
        activities,
      },
      `Successfully added ${mapped.type} activity to role`
    );
  }
  if (target.classname !== "thread") {
    return createResult(false, null, "Unsupported addActivity payload for vnext bridge");
  }
  if (
    typeof target.id !== "string" ||
    typeof activity.prestate_id !== "string" ||
    typeof activity.poststate_id !== "string"
  ) {
    return createResult(
      false,
      null,
      "Unsupported addActivity payload for vnext bridge: thread id, prestate_id and poststate_id are required"
    );
  }
  const kind = inferActionKind(activity);
  const result = addActivityVnext(legacyRad, {
    id: typeof activity.id === "string" ? activity.id : undefined,
    threadId: target.id,
    pre: activity.prestate_id,
    post: activity.poststate_id,
    kind,
    label: typeof activity.label === "string" ? activity.label : undefined,
  });
  if (!result.ok) return createResult(false, null, result.error.message);
  return createResult(
    true,
    vnextToLegacy(result.value, legacyRad),
    `Successfully added ${kind} activity to thread`
  );
};

export const addInteractionLegacy = (legacyRad, payload = {}) => {
  const sourceObj = asObject(payload.source);
  const selectedObj = asObject(payload.selected);
  const sourceActionId =
    typeof payload.sourceActionId === "string"
      ? payload.sourceActionId
      : typeof sourceObj.id === "string"
        ? sourceObj.id
        : "";
  const targetActionId =
    typeof payload.targetActionId === "string"
      ? payload.targetActionId
      : typeof selectedObj.id === "string"
        ? selectedObj.id
        : "";
  if (!sourceActionId || !targetActionId) {
    return createResult(
      false,
      null,
      "Unsupported addInteraction payload for vnext bridge: source and target interaction ids are required"
    );
  }
  const result = addInteractionVnext(legacyRad, {
    sourceActionId,
    targetActionId,
    connectionId: typeof payload.connectionId === "string" ? payload.connectionId : undefined,
  });
  if (!result.ok) return createResult(false, null, result.error.message);
  return createResult(
    true,
    vnextToLegacy(result.value, legacyRad),
    `Successfully added connection between ${sourceActionId} and ${targetActionId}`
  );
};
